import { cn } from "@/lib/utils";

export type AccessLevel = "PRO" | "FREE" | "PRO + FREE";

/**
 * Single source of truth for feature access tags shown next to resource
 * titles and page headings. Purely presentational.
 */
export function AccessBadge({
  level,
  className,
}: {
  level: AccessLevel;
  className?: string;
}) {
  const base =
    "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 py-[2px] text-[9.5px] font-bold uppercase leading-none tracking-[0.08em] align-middle";

  if (level === "FREE") {
    return (
      <span
        className={cn(
          base,
          "border-emerald-600/30 bg-emerald-500/10 text-emerald-700",
          "dark:border-emerald-400/25 dark:bg-emerald-400/10 dark:text-emerald-300",
          className,
        )}
      >
        Free
      </span>
    );
  }

  if (level === "PRO") {
    return (
      <span
        className={cn(
          base,
          "border-amber-500/40 bg-gradient-to-r from-amber-400/15 to-lime-400/15 text-amber-700 shadow-[0_0_10px_-4px_rgba(217,160,44,0.7)]",
          "dark:border-amber-300/35 dark:text-amber-200",
          className,
        )}
      >
        Pro
      </span>
    );
  }

  return (
    <span
      className={cn(
        base,
        "gap-0 border-lime-600/30 bg-lime-500/10 p-0 pr-0 text-lime-800 dark:border-lime-400/25 dark:bg-lime-400/10 dark:text-lime-200",
        className,
      )}
    >
      <span className="rounded-l-full bg-amber-500/20 px-1.5 py-[3px] text-amber-700 dark:text-amber-200">
        Pro
      </span>
      <span className="px-1 py-[3px] text-[9px] opacity-70">{" + "}</span>
      <span className="rounded-r-full bg-emerald-500/15 px-1.5 py-[3px] text-emerald-700 dark:text-emerald-300">
        Free
      </span>
    </span>
  );
}
